import { jsxs, jsx } from "react/jsx-runtime";
import assert from "assert";
import { Fragment } from "react";
import clsx from "clsx";
import { Box } from "../../Box/Box.mjs";
import { FieldLabel } from "../../FieldLabel/FieldLabel.mjs";
import { FieldMessage } from "../../FieldMessage/FieldMessage.mjs";
import { FieldOverlay } from "../FieldOverlay/FieldOverlay.mjs";
import { Stack } from "../../Stack/Stack.mjs";
import { buildDataAttributes } from "../buildDataAttributes.mjs";
import { textStyles } from "../../../css/typography.mjs";
import { touchableText } from "../../../css/typography.css.mjs";
import { Text } from "../../Text/Text.mjs";
import { mergeIds } from "../mergeIds.mjs";
import { FloatingLabel } from "./FloatingLabel.mjs";
import { secondaryIconSpace, fieldWithLabel, fieldWithoutLabel, placeholderColor, field, iconSpace, icon, iconWithValue, secondaryIcon, focusOverlay, hoverOverlay } from "./Field.css.mjs";
const Field = ({
  id,
  value,
  labelId,
  name,
  disabled,
  autoComplete,
  children,
  message,
  secondaryMessage,
  reserveMessageSpace = false,
  tone,
  "aria-describedby": ariaDescribedBy,
  data,
  secondaryIcon: secondaryIcon$1,
  alwaysShowSecondaryIcon = false,
  autoFocus,
  icon: icon$1,
  prefix,
  required,
  placeholder,
  ...restProps
}) => {
  assert(prefix === void 0 || typeof prefix === "string", "Prefix must be a string");
  const messageId = `${id}-message`;
  const fieldBackground = disabled ? { lightMode: "surface", darkMode: "neutral" } : { lightMode: "surface" };
  const hasValue = typeof value === "string" ? value.length > 0 : value != null;
  const hasTertiaryLabel = "tertiaryLabel" in restProps;
  const showSecondaryIcon = alwaysShowSecondaryIcon || secondaryIcon$1 && hasValue;
  const overlays = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      FieldOverlay,
      {
        variant: disabled ? "disabled" : "default",
        visible: tone !== "critical" || disabled
      }
    ),
    /* @__PURE__ */ jsx(FieldOverlay, { variant: "critical", visible: tone === "critical" && !disabled }),
    /* @__PURE__ */ jsx(FieldOverlay, { variant: "focus", className: focusOverlay }),
    /* @__PURE__ */ jsx(FieldOverlay, { variant: disabled ? "disabled" : "hover", className: hoverOverlay })
  ] });
  const fieldPadding = "xsmall";
  return /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
    hasTertiaryLabel ? /* @__PURE__ */ jsx(
      FieldLabel,
      {
        htmlFor: false,
        tertiaryLabel: "tertiaryLabel" in restProps ? restProps.tertiaryLabel : void 0
      }
    ) : null,
    /* @__PURE__ */ jsxs(
      Box,
      {
        position: "relative",
        background: fieldBackground,
        borderRadius: "standard",
        display: "flex",
        className: clsx(showSecondaryIcon ? secondaryIconSpace : void 0),
        children: [
          children(
            overlays,
            {
              id,
              name,
              background: fieldBackground,
              width: "full",
              paddingLeft: prefix ? "none" : fieldPadding,
              paddingRight: showSecondaryIcon ? void 0 : fieldPadding,
              borderRadius: "standard",
              outline: "none",
              "aria-describedby": mergeIds(
                ariaDescribedBy,
                message || secondaryMessage ? messageId : void 0
              ),
              "aria-required": required,
              ..."aria-label" in restProps ? { "aria-label": restProps["aria-label"] } : {},
              ..."aria-labelledby" in restProps ? { "aria-labelledby": restProps["aria-labelledby"] } : {},
              disabled,
              autoComplete,
              autoFocus,
              ...buildDataAttributes({ data, validateRestProps: restProps }),
              className: clsx(
                "label" in restProps ? fieldWithLabel : fieldWithoutLabel,
                placeholderColor,
                field,
                textStyles({
                  tone: disabled ? "disabled" : "neutral",
                  size: "standard",
                  baseline: false
                }),
                touchableText.standard,
                icon$1 && !prefix ? iconSpace : null
              )
            },
            icon$1 ? /* @__PURE__ */ jsx(
              Box,
              {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                height: "touchable",
                width: "touchable",
                pointerEvents: "none",
                left: 0,
                className: clsx(icon, value && iconWithValue),
                children: /* @__PURE__ */ jsx(
                  Text,
                  {
                    baseline: false,
                    tone: disabled ? "disabled" : prefix ? "secondary" : void 0,
                    align: "left",
                    children: icon$1
                  }
                )
              }
            ) : null,
            showSecondaryIcon ? /* @__PURE__ */ jsx(
              Box,
              {
                component: "span",
                position: "absolute",
                width: "touchable",
                height: "touchable",
                display: "flex",
                marginRight: "xxsmall",
                alignItems: "center",
                justifyContent: "center",
                right: 0,
                className: clsx(secondaryIcon),
                children: secondaryIcon$1
              }
            ) : null,
            prefix && value ? /* @__PURE__ */ jsx(
              Box,
              {
                component: "label",
                htmlFor: id,
                display: "flex",
                paddingLeft: icon$1 ? void 0 : fieldPadding,
                height: "touchable",
                flexShrink: 0,
                className: clsx(
                  "label" in restProps ? fieldWithLabel : fieldWithoutLabel,
                  field,
                  icon$1 ? iconSpace : null
                ),
                children: /* @__PURE__ */ jsx(Text, { tone: "secondary", baseline: false, children: prefix })
              }
            ) : null
          ),
          "label" in restProps && /* @__PURE__ */ jsx(
            FloatingLabel,
            {
              label: restProps.label,
              hasIcon: Boolean(icon$1),
              required,
              isFloating: hasValue,
              htmlFor: id,
              disabled
            }
          )
        ]
      }
    ),
    message || secondaryMessage || reserveMessageSpace ? /* @__PURE__ */ jsx(
      FieldMessage,
      {
        id: messageId,
        tone,
        disabled,
        message,
        secondaryMessage,
        reserveMessageSpace
      }
    ) : null
  ] });
};
export {
  Field
};
